import React, { useState, useCallback } from 'react';
import {
  Box, Card, Typography, TextField, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, CircularProgress, Grid, Dialog, DialogTitle, DialogContent,
  DialogActions, IconButton, Tooltip,
} from '@mui/material';
import { Add, Refresh, Delete, ArrowBack } from '@mui/icons-material';
import { InvokeApi, apiList } from '../../services/ApiService';
import { Config } from '../../config/Config';
import ClassSectionGrid from '../../components/ClassSectionGrid';

export default function Subjects() {
  const [view, setView] = useState('grid');
  const [selected, setSelected] = useState(null);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialog, setDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [subjectName, setSubjectName] = useState('');

  const load = useCallback(async (cls) => {
    if (!cls) return;
    setLoading(true);
    const data = await InvokeApi(Config.main_url + apiList.getSubjects, {
      standard: cls.standard, section: cls.section,
    });
    setRecords(data?.subjects || []);
    setLoading(false);
  }, []);

  const handleSelect = (cls) => {
    setSelected(cls);
    setView('list');
    load(cls);
  };

  const handleSave = async () => {
    if (!subjectName.trim()) { alert('Subject name is required'); return; }
    setSaving(true);
    const data = await InvokeApi(Config.main_url + apiList.addSubject, {
      name: subjectName.trim(),
      standard: selected.standard,
      section: selected.section,
      academic_year: selected.acYear,
      status: 'Active',
      createdBy: '1',
    });
    setSaving(false);
    if (data?.code === '200') { setDialog(false); setSubjectName(''); load(selected); }
    else alert(data?.message || 'Failed');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this subject?')) return;
    await InvokeApi(Config.main_url + apiList.deleteSubject, { id });
    load(selected);
  };

  if (view === 'grid') {
    return (
      <Box>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" fontWeight={800} color="#1a237e">Subjects</Typography>
          <Typography variant="body2" color="text.secondary">Select a class to manage subjects</Typography>
        </Box>
        <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', p: 3 }}>
          <ClassSectionGrid onSelect={handleSelect} />
        </Card>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={800} color="#1a237e">Subjects</Typography>
          <Typography variant="body2" color="text.secondary">
            {selected?.standard} - {selected?.section} · {records.length} subjects
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<ArrowBack />} onClick={() => setView('grid')}
            sx={{ borderRadius: 2, color: '#1a237e', borderColor: '#1a237e' }}>Back</Button>
          <Tooltip title="Refresh"><IconButton onClick={() => load(selected)} sx={{ border: '1px solid #e8eaf6', borderRadius: 2, color: '#1a237e' }}><Refresh /></IconButton></Tooltip>
          <Button variant="contained" startIcon={<Add />} onClick={() => { setSubjectName(''); setDialog(true); }}
            sx={{ background: 'linear-gradient(135deg,#1a237e,#1565c0)', borderRadius: 2, fontWeight: 700 }}>Add Subject</Button>
        </Box>
      </Box>

      <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress sx={{ color: '#1a237e' }} /></Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ background: '#f5f6fa' }}>
                  {['#', 'Subject Name', 'Status', 'Actions'].map(h => (
                    <TableCell key={h} sx={{ fontWeight: 700, color: '#1a237e', fontSize: 13 }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {records.length === 0 ? (
                  <TableRow><TableCell colSpan={4} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                    No subjects for {selected?.standard} - {selected?.section}
                  </TableCell></TableRow>
                ) : records.map((r, i) => (
                  <TableRow key={r.ID || i} hover>
                    <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>{i + 1}</TableCell>
                    <TableCell sx={{ fontSize: 14, fontWeight: 600 }}>{r.name}</TableCell>
                    <TableCell><Chip label={r.status || 'Active'} size="small" sx={{ background: '#e8f5e9', color: '#2e7d32', fontWeight: 600 }} /></TableCell>
                    <TableCell>
                      <Tooltip title="Delete"><IconButton size="small" onClick={() => handleDelete(r.ID)} sx={{ color: '#c62828' }}><Delete fontSize="small" /></IconButton></Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      <Dialog open={dialog} onClose={() => setDialog(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, color: '#1a237e', borderBottom: '1px solid #f0f0f0' }}>
          Add Subject — {selected?.standard} - {selected?.section}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <TextField fullWidth size="small" label="Subject Name *" value={subjectName}
            onChange={e => setSubjectName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSave()} autoFocus />
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1, borderTop: '1px solid #f0f0f0' }}>
          <Button onClick={() => setDialog(false)} variant="outlined" sx={{ borderRadius: 2 }}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}
            sx={{ background: 'linear-gradient(135deg,#1a237e,#1565c0)', borderRadius: 2, fontWeight: 700 }}>
            {saving ? <CircularProgress size={20} color="inherit" /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
